import { chunk, filter, head, map, pipe, take, toArray, zipWithIndex } from '@fxts/core';
import { html, rune, View } from 'rune-ts';
import { CreatorTypeS } from '../../../../features/Creator/outbound/share';
import {
  makeCreatorScreenNavigate,
  makeEmptyScreenNavigate,
  makeUtilScreenNavigate,
} from '../../../../shared/app/navigate';
import { MShopThinIcon } from '../../atoms/Icon';
import klass from './CreatorRankingMain.module.scss';
import { font, staticFont } from '../../../../shared/typography';
import { dataStr } from '../../../../shared/util/dataStr';
import { differenceInMinutes } from 'date-fns';
import { SectionTitle } from '../SectionTitle/SectionTitle';
import { SeeMoreLinkPlugin } from '../SectionTitle/plugins';
import { RankDiff } from '@services/MarppleShop/renderApp/components/atoms/RankDiff/RankDiff.ts';
import { BaseCreatorRankerCard } from '@services/MarppleShop/renderApp/components/cells/CreatorRanking/BaseCreatorRankerCard.ts';
import { getResizedUrl } from '@services/MarppleShop/shared/util/image.ts';
import { makePlusUrl } from '@services/MarppleShop/shared/util/url.ts';
import { getSharedData } from '@services/MarppleShop/shared/util/rune.ts';
import { isAllCrewById } from '@services/MarppleShop/shared/util';

export type CreatorRankingMainProps = {
  creator_rankings: CreatorTypeS.ICreatorRanking[];
  updated_at: Date;
};

class CreatorTopRankerCard extends BaseCreatorRankerCard {
  protected override template() {
    const { name, seller_type_name, domain_name } = this.data.creator_ranking;
    const sharedData = getSharedData(this);

    return this.createContainerWithBackdrop(
      html`
        <a
          href="/${ET.lang}/${domain_name}"
          data-post-message="${dataStr(
            makeEmptyScreenNavigate(
              sharedData?.plus_crew && isAllCrewById(sharedData.plus_crew.id)
                ? `/${ET.lang}/${domain_name}`
                : makePlusUrl(sharedData?.plus_crew?.domain_name, `/${ET.lang}/${domain_name}`),
            ),
          )}"
          class="${klass.main}"
        >
          <div class="${klass.header}">
            <span class="${staticFont('bebas_24_bold', 'bebas_20_bold')}">REALTIME RANKING</span>
            <div class="${klass.rank}">
              <span class="${staticFont('bebas_60_bold', 'bebas_48_bold')}">TOP 1</span>
              ${new RankDiff({
                ranking: this.data.creator_ranking,
                font: staticFont('unica_24_bold'),
                klass: klass.rank,
              })}
            </div>
          </div>
          <div class="${klass.bottom}">
            <div class="${klass.information}">
              <span class="${klass.name_wrapper}">
                <span class="${klass.name} ${font('32_bold', '24_bold')}">${name}</span>
                <span class="${klass.icon}">${MShopThinIcon}</span>
              </span>
              <span class="${klass.type} ${font('14_medium')}">${seller_type_name || ''}</span>
            </div>
            <div class="${klass.products_container}">${this.makeProductImages()}</div>
          </div>

          ${this._makeHeartImage()}
        </a>
      `,
      klass.creator_top_ranker_card,
    );
  }

  private _makeHeartImage() {
    return html`
      <div class="${klass.thumbnail}">
        <div class="${klass.thumbnail_mask}">
          <img src="${this.makeThumbnailImageUrl()}" alt="" />
        </div>
      </div>
    `;
  }

  private makeThumbnailImageUrl() {
    return getResizedUrl({
      url: this.data.creator_ranking.thumbnail_img || this.defaultImage,
      quality: 80,
      format: 'webp',
      width: 1200,
      bg: '#FFFFFF',
    });
  }
}

class CreatorRankerCard extends View<{
  creator_ranking: CreatorTypeS.ICreatorRanking;
}> {
  protected override template() {
    const { current_rank, domain_name, seller_type_name, name } = this.data.creator_ranking;

    return html`
      <a
        data-post-message="${dataStr(makeUtilScreenNavigate(`/${ET.lang}/${domain_name}`))}"
        href="/${ET.lang}/${domain_name}"
        class="${klass.creator_ranker_card}"
      >
        <div class="${klass.rank}">
          <span class="${staticFont('bebas_24_bold', 'bebas_20_bold')}">${current_rank}</span>
          ${new RankDiff({
            ranking: this.data.creator_ranking,
            font: staticFont('unica_14_bold'),
            klass: klass.rank,
          })}
        </div>
        <img class="${klass.thumbnail}" src="${this.makeThumbnailImageUrl()}" alt="thumbnail_img" />
        <div class="${klass.name}">
          <span class="${font('16_bold', '16_medium')}">${html.preventEscape(name || '')}</span>
          <span class="${font('14_medium')}">${seller_type_name || ''}</span>
        </div>
      </a>
    `;
  }

  private makeThumbnailImageUrl() {
    return getResizedUrl({
      url: this.data.creator_ranking.thumbnail_img || this.defaultImage,
      quality: 80,
      format: 'webp',
      width: 300,
      bg: '#FFFFFF',
    });
  }

  get defaultImage() {
    return '//s3.marpple.co/files/u_2283830/2024/4/original/66bc95a0c03a6e46c874809d11e592760e7423ae1.png';
  }

  get isMobile(): boolean {
    return !!rune.getSharedData(this)?.is_mobile;
  }
}

class CreatorRankerList extends View<{
  creator_rankings: CreatorTypeS.ICreatorRanking[];
}> {
  protected override template() {
    return html` <div class="${klass.creator_ranker_list}">${this._makeCreatorRankingRows()}</div>`;
  }

  private _makeCreatorRankingRows() {
    return pipe(
      this.data.creator_rankings,
      // 모바일의 경우 1위를 제외한 9개만 렌더링
      filter(({ current_rank }) => current_rank !== 1),
      chunk(3),
      zipWithIndex,
      map(
        ([index, creator_rankings]) => html`
          <div class="${klass.row} ${this.getLineClass(index)}">
            ${creator_rankings.map((creator_ranking) => new CreatorRankerCard({ creator_ranking }))}
          </div>
        `,
      ),
      toArray,
    );
  }

  private getLineClass(line: number) {
    if (line === 0) return klass.first;
    if (line === 1) return klass.middle;
    if (line === 2) return klass.last;
    return '';
  }

  get isMobile(): boolean {
    return !!rune.getSharedData(this)?.is_mobile;
  }
}

export class CreatorRankingMain extends View<CreatorRankingMainProps> {
  override template() {
    const { top_one, top_ten } = this._makeTopCreatorData();
    const { texts } = this;

    return html`<div class="${klass.creator_ranking_container}">
      <div class="${klass.title}">
        ${new SectionTitle({
          plugins: [
            new SeeMoreLinkPlugin({
              navigate: texts.navigate,
              see_more_link: texts.see_more_link,
              size: this.isMobile ? 'small' : 'xLarge',
            }),
          ],
          title_props: {
            title: texts.title,
            sub_title: texts.description,
            description: this.updatedTime,
            size: this.isMobile ? 'large' : 'xLarge',
          },
        })}
      </div>

      <div class="${klass.creator_ranking}">
        <div class="${klass.creator_top_ranker_card_container}">
          ${new CreatorTopRankerCard({ creator_ranking: top_one })}
        </div>
        ${new CreatorRankerList({ creator_rankings: top_ten })}
      </div>
    </div> `;
  }

  private _makeTopCreatorData = (): {
    top_one: CreatorTypeS.ICreatorRanking;
    top_ten: CreatorTypeS.ICreatorRanking[];
  } => {
    const { creator_rankings } = this.data;

    const top_ten = pipe(creator_rankings, take(10), toArray);

    const top_one = head(creator_rankings)!;

    return { top_one, top_ten };
  };

  get updatedTime() {
    const minutes_diff = Math.abs(differenceInMinutes(new Date(this.data.updated_at), new Date()));

    return ET('mps2::main::creator_ranking::updated_at', { minute: minutes_diff });
  }

  get texts() {
    return {
      title: ET('mps2::main::creator_ranking::title'),
      description: ET('mps2::main::creator_ranking::description'),
      see_more_link: `/${ET.lang}/@/creator/ranking/realtime`,
      navigate: makeCreatorScreenNavigate(`/${ET.lang}/@/creator/ranking/realtime`),
      see_more_text: ET('mps2::main::creator_ranking::see_more'),
    };
  }

  get isMobile(): boolean {
    return !!rune.getSharedData(this)?.is_mobile;
  }
}
